export interface AudioPlaybackState {
  isPlaying: boolean;
  isLoading: boolean;
  error: string | null;
}

const IDLE_STATE: AudioPlaybackState = {
  isPlaying: false,
  isLoading: false,
  error: null,
};

export class AudioPlaybackManager {
  private audioElement: HTMLAudioElement | null = null;
  private currentObjectUrl: string | null = null;
  private abortController: AbortController | null = null;
  private currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private listeners: Set<() => void> = new Set();
  private _state: AudioPlaybackState = { ...IDLE_STATE };
  private analyserService: {
    connectAudioElement: (audio: HTMLAudioElement) => void;
  } | null = null;

  setAnalyserService(
    service: { connectAudioElement: (audio: HTMLAudioElement) => void } | null
  ): void {
    this.analyserService = service;
  }

  get state(): AudioPlaybackState {
    return this._state;
  }

  async playFromFetch(fetchFn: () => Promise<Response>): Promise<void> {
    this.stop();

    const abortController = new AbortController();
    this.abortController = abortController;

    this.setState({ isPlaying: false, isLoading: true, error: null });

    try {
      const response = await fetchFn();

      if (abortController.signal.aborted) return;

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      await this.loadAndPlayStream(response);
    } catch (error) {
      if (abortController.signal.aborted) return;
      this.cleanup();
      const message =
        error instanceof Error ? error.message : 'Failed to fetch audio';
      this.setState({ isPlaying: false, isLoading: false, error: message });
    }
  }

  async playSequence(
    audioFetches: AsyncIterable<() => Promise<Response>>
  ): Promise<void> {
    this.stop();

    const abortController = new AbortController();
    this.abortController = abortController;
    this.setState({ isPlaying: false, isLoading: true, error: null });

    try {
      for await (const fetchFn of audioFetches) {
        if (abortController.signal.aborted) return;

        const response = await fetchFn();
        if (abortController.signal.aborted) return;

        if (!response.ok) {
          throw new Error(`TTS request failed: ${response.status}`);
        }

        await this.playSegment(response, abortController);
        if (abortController.signal.aborted) return;
      }

      this.cleanup();
      this.setState({ ...IDLE_STATE });
    } catch (error) {
      if (abortController.signal.aborted) return;
      this.cleanup();
      const message =
        error instanceof Error ? error.message : 'Failed to play audio';
      this.setState({ isPlaying: false, isLoading: false, error: message });
    }
  }

  stop(): void {
    this.abortController?.abort();
    this.abortController = null;
    if (this.audioElement) {
      this.audioElement.dispatchEvent(new Event('ended'));
    }
    this.cleanup();
    this.setState({ ...IDLE_STATE });
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): AudioPlaybackState {
    return this._state;
  }

  private async playSegment(
    response: Response,
    abortController: AbortController
  ): Promise<void> {
    const blob = await response.blob();
    if (abortController.signal.aborted) return;

    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }
    if (this.audioElement) {
      this.audioElement.removeEventListener('ended', this.handleAudioEnded);
      this.audioElement.removeEventListener('error', this.handleAudioError);
      this.audioElement.pause();
    }

    const objectUrl = URL.createObjectURL(blob);
    this.currentObjectUrl = objectUrl;

    const audio = new Audio(objectUrl);
    this.audioElement = audio;
    this.analyserService?.connectAudioElement(audio);

    await new Promise<void>((resolve, reject) => {
      audio.addEventListener('ended', () => {
        this.setState({ isPlaying: false, isLoading: false, error: null });
        resolve();
      });
      audio.addEventListener('error', () => {
        this.setState({
          isPlaying: false,
          isLoading: false,
          error: 'Segment playback failed',
        });
        reject(new Error('Segment playback failed'));
      });
      this.setState({ isPlaying: true, isLoading: false, error: null });
      audio.play().then(
        () => {},
        (playError) => {
          this.setState({
            isPlaying: false,
            isLoading: false,
            error: 'Play failed',
          });
          reject(playError);
        }
      );
    });
  }

  private async loadAndPlayStream(response: Response): Promise<void> {
    // Safari has limited MSE support for audio/mpeg, so the blob path remains the safe fallback.
    if (
      typeof MediaSource === 'undefined' ||
      !MediaSource.isTypeSupported('audio/mpeg')
    ) {
      return this.loadAndPlayBlob(response);
    }

    if (!response.body) {
      return this.loadAndPlayBlob(response);
    }

    try {
      const mediaSource = new MediaSource();
      const objectUrl = URL.createObjectURL(mediaSource);
      this.currentObjectUrl = objectUrl;

      const audio = new Audio(objectUrl);

      this.audioElement = audio;
      this.analyserService?.connectAudioElement(audio);

      await new Promise<void>((resolve, reject) => {
        this.audioElement?.addEventListener('ended', () => {
          this.handleAudioEnded();
          resolve();
        });
        this.audioElement?.addEventListener('error', () => {
          this.handleAudioError();
          reject(new Error('Playback failed'));
        });

        mediaSource.addEventListener('sourceopen', () => {
          this.initSourceBuffer(mediaSource, response, audio).catch(reject);
        });
      });
    } catch (error) {
      this.cleanup();
      const message =
        error instanceof Error ? error.message : 'Failed to play audio';
      this.setState({ isPlaying: false, isLoading: false, error: message });
    }
  }

  private async initSourceBuffer(
    mediaSource: MediaSource,
    response: Response,
    audio: HTMLAudioElement
  ): Promise<void> {
    let sourceBuffer: SourceBuffer;
    try {
      sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
    } catch {
      // addSourceBuffer can throw if the MIME type is not supported at runtime.
      await this.loadAndPlayBlob(response);
      return;
    }

    const pendingChunks: Uint8Array<ArrayBuffer>[] = [];
    let streamDone = false;
    let playbackStarted = false;

    const appendNextChunk = () => {
      if (sourceBuffer.updating) return;

      if (pendingChunks.length > 0) {
        const chunk = pendingChunks.shift()!;
        sourceBuffer.appendBuffer(chunk);
        return;
      }

      if (streamDone && mediaSource.readyState === 'open') {
        mediaSource.endOfStream();
      }
    };

    // Resolve once playback ends; continue draining the queue after that.
    await new Promise<void>((resolve, reject) => {
      sourceBuffer.addEventListener('updateend', async () => {
        if (!playbackStarted) {
          playbackStarted = true;
          try {
            await audio.play();
            this.setState({ isPlaying: true, isLoading: false, error: null });
          } catch (playError) {
            this.setState({
              isPlaying: false,
              isLoading: false,
              error: 'Streaming play failed',
            });
            reject(playError);
            return;
          }
        }
        appendNextChunk();
      });

      this.pumpStreamToBuffer(response, pendingChunks, appendNextChunk)
        .then(() => {
          streamDone = true;
          appendNextChunk();
        })
        .catch((readError) => {
          if (!this.abortController?.signal.aborted) {
            reject(readError);
          }
        });
    });
  }

  private async pumpStreamToBuffer(
    response: Response,
    pendingChunks: Uint8Array<ArrayBuffer>[],
    appendNextChunk: () => void
  ): Promise<void> {
    const reader = response.body!.getReader();
    this.currentReader = reader;

    while (true) {
      if (this.abortController?.signal.aborted) {
        reader.cancel();
        return;
      }

      const { done, value } = await reader.read();

      if (done) break;

      pendingChunks.push(value);
      appendNextChunk();
    }
  }

  private async loadAndPlayBlob(response: Response): Promise<void> {
    try {
      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);
      this.currentObjectUrl = objectUrl;

      const audio = new Audio(objectUrl);
      this.audioElement = audio;
      this.analyserService?.connectAudioElement(audio);
      await new Promise<void>((resolve, reject) => {
        this.audioElement?.addEventListener('ended', () => {
          this.handleAudioEnded();
          resolve();
        });
        this.audioElement?.addEventListener('error', () => {
          this.handleAudioError();
          reject(new Error('Blob playback failed'));
        });

        this.audioElement?.play().then(() => {
          this.setState({ isPlaying: true, isLoading: false, error: null });
        }, reject);
      });
    } catch (error) {
      this.cleanup();
      const message =
        error instanceof Error ? error.message : 'Failed to play audio';
      this.setState({ isPlaying: false, isLoading: false, error: message });
    }
  }

  private handleAudioEnded = (): void => {
    this.cleanup();
    this.setState({ ...IDLE_STATE });
  };

  private handleAudioError = (): void => {
    this.cleanup();
    this.setState({
      isPlaying: false,
      isLoading: false,
      error: 'Audio playback failed',
    });
  };

  private cleanup(): void {
    this.currentReader?.cancel();
    this.currentReader = null;

    if (this.audioElement) {
      this.audioElement.removeEventListener('ended', this.handleAudioEnded);
      this.audioElement.removeEventListener('error', this.handleAudioError);
      this.audioElement.pause();
      this.audioElement.removeAttribute('src');
      this.audioElement = null;
    }

    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }

  private setState(newState: AudioPlaybackState): void {
    this._state = newState;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
